import Cookies from 'js-cookie';

import { Urls } from 'js/src/client';
import { AuthQueryParams } from 'js/src/components/auth/auth-consts';
import { getQueryParam } from 'js/src/libs/querystring';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';

/**
 * @param navigationRequired: Flag to define if navigation is required on invalid channel name.
 * TODO: while converting to TS I realized the regex string here is almost certainly incorrect as it's returning too much
 */
export function getWorkspaceSlugFromPath(navigationRequired = true): string | null {
    const match = /\/user\/(.*)\//.exec(window.location.pathname);
    if (match === null || match.length < 2) {
        if (navigationRequired) {
            alert('Could not find that channel!');
            window.location.href = Urls.DASHBOARD;
        }
        return null;
    }
    return match[1];
}

// top most common email domains from our mixpanel
const COMMON_EMAIL_REGEXPS = [
    /gmail\.com$/,
    /hotmail\.com$/,
    /yahoo\.com$/,
    /qq\.com$/,
    /outlook\.com$/,
    /mail\.ru$/,
    /icloud\.com$/,
    /163\.com$/,
    /yandex\.ru$/,
    /naver\.com$/,
    /gamil\.com$/,
    /web\.de$/,
    /gmx\.de$/,
    /gmai\.com$/,
    /live\.com$/,
    /hotmail\.fr$/,
    /guru\.smp\.belajar\.id$/,
    /yopmail\.com$/,
    /gufum\.com$/,
    /@yahoo\..*$/,
    /edu$/,
    /\.edu\./,
    /student\./,
    /example.com$/,
];

export const emailIsProbablyBusiness = (emailDomain: string): boolean => {
    return !COMMON_EMAIL_REGEXPS.some((regexp) => regexp.test(emailDomain));
};

export const userProbablyHasBusinessEmail = (): boolean => {
    return emailIsProbablyBusiness(SERVER?.USER?.email || '');
};

/**
 * Social login only starts from a POST, so submit the begin URL as a form rather than navigating to it.
 * social-core reads `next` from the POST body alone, so the URL's query parameters travel as fields.
 */
export const startSocialAuth = (socialAuthUrl: string, target?: string) => {
    const [path, queryString = ''] = socialAuthUrl.split('?');
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = path;
    form.hidden = true;
    if (target) {
        form.target = target;
    }
    const fields = [
        ...new URLSearchParams(queryString),
        ['csrfmiddlewaretoken', Cookies.get('csrftoken') ?? ''],
    ];
    for (const [name, value] of fields) {
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.value = value;
        form.appendChild(field);
    }
    document.body.appendChild(form);
    form.submit();
    form.remove();
};

export const buildSocialAuthUrl = (socialAuthUrl: string, sourceRedirectUrl: string) => {
    const invite_uuid = getQueryParam(AuthQueryParams.INVITE_UUID);
    let innerNext = sourceRedirectUrl; // e.g. "/login/?is_signup=1[&other_params]"
    innerNext = QueryStringManipulator.updateQueryStringParameter(innerNext, 'use_redirect', '1');
    if (invite_uuid) {
        innerNext = QueryStringManipulator.updateQueryStringParameter(
            innerNext,
            AuthQueryParams.INVITE_UUID,
            invite_uuid,
        );
    }

    const url = QueryStringManipulator.updateQueryStringParameter(
        socialAuthUrl,
        'next',
        encodeURIComponent(innerNext),
    );

    return url;
};

/**
 * The path the auth page is sitting on, carrying only `next` — the destination a user was sent
 * here to reach. Other params on the page are deliberately left behind.
 */
export const getCurrentPathWithNext = (): string => {
    const next = getQueryParam(AuthQueryParams.NEXT);
    if (!next) {
        return window.location.pathname;
    }
    return `${window.location.pathname}?${AuthQueryParams.NEXT}=${encodeURIComponent(next)}`;
};
